
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { Download, Package, Shield, Grid3X3, CheckCircle, Settings } from "lucide-react";
import { DeviceSidebar } from "./DeviceSidebar";
import { DeviceAppsContent } from "./DeviceAppsContent";
import { DeviceAppsCategoriesContent } from "./DeviceAppsCategoriesContent";
import { DeviceAppsSettingsContent } from "./DeviceAppsSettingsContent";
import { useDeviceLevelData } from "../hooks/useDeviceLevelData";
import { ConvexErrorBoundary } from "./ConvexErrorBoundary";

const appsSidebarItems = [
  { id: "settings", label: "Settings", icon: Settings },
  { id: "categories", label: "App Categories", icon: Grid3X3 },
  { id: "whitelist", label: "App Whitelist", icon: CheckCircle },
  { id: "blacklist", label: "App Blacklist", icon: Shield },
  { id: "available", label: "All Available Apps", icon: Download },
  { id: "installed", label: "Installed Apps", icon: Package },
];

interface DeviceAppsTabProps {
  deviceId?: string;
}

export const DeviceAppsTab = ({ deviceId }: DeviceAppsTabProps) => {
  const [activeAppsSection, setActiveAppsSection] = useState("categories");
  const { level } = useDeviceLevelData(deviceId || '');
  const { toast } = useToast();
  
  // Get device details and app configuration
  const deviceData = useQuery(api.devices.get.details, deviceId ? { device: deviceId as any } : "skip");
  const deviceAppConfig = useQuery(api.devices.apps.getConfig, 
    deviceData?.device?.deviceConfig ? { deviceConfig: deviceData.device.deviceConfig } : "skip"
  );
  
  // Mutation for updating device apps
  const updateDeviceApps = useMutation(api.devices.apps.update);

  // Check if device is using Apple App Store 
  const isUsingAppleAppStore = (deviceAppConfig?.appFilter as any) === true;

  // Filter sidebar items based on app store selection
  const filteredSidebarItems = isUsingAppleAppStore 
    ? appsSidebarItems.filter(item => item.id === "blacklist" || item.id === "installed" || item.id === "settings")
    : appsSidebarItems;

  // Handle app filter change 
  const handleAppFilterChange = async (appFilterValue: Record<string, boolean | { exclude?: { tags: string[]; type: "any" | "all" }; include?: { tags: string[]; type: "any" | "all" } }> | true) => {
    if (!deviceId) return;
    
    try {
      await updateDeviceApps({
        device: deviceId as any,
        appFilter: appFilterValue,
      });
      
      toast({
        title: "App Settings Updated",
        description: "Device app filtering has been updated successfully.",
      });
    } catch (error) {
      console.error('Error updating app filter:', error);
      toast({
        title: "Error",
        description: "Failed to update app settings.",
        variant: "destructive",
      });
    }
  };

  // Handle app store change
  const handleAppStoreChange = async (useAppleStore: boolean) => {
    if (!deviceId) return;
    
    try {
      if (useAppleStore) {
        // For Apple App Store mode, set appFilter with all categories to true
        // This allows all apps to be installed
        await updateDeviceApps({
          device: deviceId as any,
          appFilter: true as any,
        });
        
        toast({
          title: "App Store Changed",
          description: "Device switched to Apple App Store. All apps are now allowed.",
        });
      } else {
        // Reset to Kosher App Store mode with empty filter (use level defaults)
        await updateDeviceApps({
          device: deviceId as any,
          appFilter: {},
        });
        
        toast({
          title: "App Store Changed", 
          description: "Device switched to Kosher App Store. Level restrictions now apply.",
        });
      }
    } catch (error) {
      console.error('Error updating app store:', error);
      toast({
        title: "Error",
        description: "Failed to change app store settings.",
        variant: "destructive",
      });
    }
  };

  // Switch to blacklist if current section is not available in Apple App Store mode
  useEffect(() => {
    if (isUsingAppleAppStore && !["blacklist", "installed", "settings"].includes(activeAppsSection)) {
      setActiveAppsSection("blacklist");
    }
  }, [isUsingAppleAppStore, activeAppsSection]);

  const renderContent = () => {
    if (activeAppsSection === "categories") {
      return <DeviceAppsCategoriesContent deviceId={deviceId || ''} />;
    }
    
    if (activeAppsSection === "settings") {
      return (
        <DeviceAppsSettingsContent
          deviceId={deviceId}
          isUsingAppleAppStore={isUsingAppleAppStore}
          onAppStoreChange={handleAppStoreChange}
          onAppFilterChange={handleAppFilterChange}
        />
      );
    }
    
    return <DeviceAppsContent activeSection={activeAppsSection} deviceId={deviceId} />;
  };

  return (
    <ConvexErrorBoundary>
      <div className="flex h-full">
        <DeviceSidebar
        title="Apps"
        items={filteredSidebarItems}
        activeSection={activeAppsSection}
        onSectionChange={setActiveAppsSection}
      />
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto p-3">
          {renderContent()}
        </div>
      </div>
    </div>
    </ConvexErrorBoundary>
  );
};
